import { getAnonSessionId } from "./anonSession";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";

export class ApiError extends Error {
  status: number;
  code?: string;
  path?: string;
  method?: string;
  /** Backend quota gates (403 with detail.upgrade_required) — callers should
   *  surface a Premium CTA, not a generic failure toast. */
  upgradeRequired?: boolean;
  constructor(
    message: string,
    init: { status: number; code?: string; path?: string; method?: string; upgradeRequired?: boolean }
  ) {
    super(message);
    this.name = "ApiError";
    this.status = init.status;
    this.code = init.code;
    this.path = init.path;
    this.method = init.method;
    this.upgradeRequired = init.upgradeRequired;
  }
}

interface FetchOptions extends RequestInit {
  token?: string;
  _skipRefresh?: boolean; // internal: prevent infinite refresh loops
}

// Mutex to prevent multiple simultaneous refresh attempts
let refreshPromise: Promise<string | null> | null = null;

async function tryRefreshToken(): Promise<string | null> {
  if (typeof window === "undefined") return null;

  // Read refresh token from persisted store
  const raw = localStorage.getItem("velyq-auth");
  if (!raw) return null;

  let refreshToken: string | null = null;
  try {
    const parsed = JSON.parse(raw);
    refreshToken = parsed?.state?.refreshToken;
  } catch {
    return null;
  }

  if (!refreshToken) return null;

  try {
    const res = await fetch(`${API_URL}/api/v1/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!res.ok) return null;

    const tokens: { access_token: string; refresh_token: string } = await res.json();

    // Update store — import dynamically to avoid circular deps
    const { useAuthStore } = await import("@/lib/store");
    useAuthStore.getState().updateTokens(tokens.access_token, tokens.refresh_token);

    return tokens.access_token;
  } catch {
    return null;
  }
}

function forceLogout() {
  if (typeof window === "undefined") return;
  localStorage.removeItem("velyq-auth");
  window.location.href = "/login";
}

export async function api<T>(
  endpoint: string,
  options: FetchOptions = {}
): Promise<T> {
  const { token, _skipRefresh, ...fetchOptions } = options;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  // Merge existing headers
  if (options.headers) {
    const existingHeaders = options.headers as Record<string, string>;
    Object.assign(headers, existingHeaders);
  }

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  // Cookieless guest funnel: echo the stored anon-session id on every call so
  // backend claim helpers (/auth/register, /auth/login) can attach pre-signup
  // ATS rows without the deprecated third-party cookie. No-op when unset, and
  // never overrides an explicit caller-set header.
  if (!headers["X-Anon-Session"]) {
    const anon = getAnonSessionId();
    if (anon) headers["X-Anon-Session"] = anon;
  }

  const response = await fetch(`${API_URL}/api/v1${endpoint}`, {
    ...fetchOptions,
    headers,
  });

  if (!response.ok) {
    // Handle 401 Unauthorized — try refreshing the token first
    if (response.status === 401 && token && !_skipRefresh) {
      // Use mutex so concurrent 401s share a single refresh
      if (!refreshPromise) {
        refreshPromise = tryRefreshToken().finally(() => {
          refreshPromise = null;
        });
      }
      const newToken = await refreshPromise;

      if (newToken) {
        // Retry the original request with the new token
        return api<T>(endpoint, { ...options, token: newToken, _skipRefresh: true });
      }

      // Refresh failed — force logout. Throw as ApiError(status=401) so
      // reportSettledRejections + handleApiError can skip it (normal logout
      // flow, not a real error worth capturing in PostHog).
      forceLogout();
      throw new ApiError("Session expirée", { status: 401, code: "session_expired", path: endpoint, method: options?.method ?? "GET" });
    }

    // Other 401 (no token or refresh already failed)
    if (response.status === 401) {
      forceLogout();
      throw new ApiError("Session expirée", { status: 401, code: "session_expired", path: endpoint, method: options?.method ?? "GET" });
    }

    const errorBody = await response.json().catch(() => ({} as Record<string, unknown>));
    const detail = (errorBody as { detail?: unknown }).detail;
    const message = typeof detail === "string"
      ? detail
      : (detail as { message?: string } | undefined)?.message ?? "Une erreur est survenue";
    const code = typeof detail === "object" && detail !== null
      ? (detail as { code?: string }).code
      : undefined;
    throw new ApiError(message, {
      status: response.status,
      code,
      path: endpoint,
      method: options?.method ?? "GET",
    });
  }

  // Handle 204 No Content (e.g. DELETE responses)
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

/**
 * Drop-in replacement for fetch() that handles 401 with automatic token refresh.
 * Use this instead of raw fetch() for authenticated requests.
 */
export async function authFetch(
  url: string,
  token: string,
  init?: RequestInit
): Promise<Response> {
  const doFetch = (t: string) =>
    fetch(url, {
      ...init,
      headers: {
        ...init?.headers,
        Authorization: `Bearer ${t}`,
      },
    });

  let response = await doFetch(token);

  if (response.status === 401) {
    if (!refreshPromise) {
      refreshPromise = tryRefreshToken().finally(() => { refreshPromise = null; });
    }
    const newToken = await refreshPromise;
    if (newToken) {
      response = await doFetch(newToken);
    } else {
      forceLogout();
    }
  }

  return response;
}

// Detect browser language (fr or en)
export function detectBrowserLanguage(): "fr" | "en" {
  if (typeof navigator === "undefined") return "fr";
  const lang = navigator.language || (navigator as { userLanguage?: string }).userLanguage || "fr";
  return lang.toLowerCase().startsWith("fr") ? "fr" : "en";
}

// Auth endpoints
export const auth = {
  register: (data: { email: string; password: string; first_name: string; last_name: string; ui_language?: "fr" | "en"; recaptcha_token?: string }) =>
    api("/auth/register", { method: "POST", body: JSON.stringify(data) }),

  login: (data: { email: string; password: string; recaptcha_token?: string }) =>
    api<{ access_token: string; refresh_token: string }>("/auth/login", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  refresh: (refreshToken: string) =>
    api<{ access_token: string; refresh_token: string }>("/auth/refresh", {
      method: "POST",
      body: JSON.stringify({ refresh_token: refreshToken }),
    }),
};

// User endpoints
export const users = {
  me: (token: string) => api("/users/me", { token }),

  // Onboarding status + welcome-seen go through api() so they get the
  // 401→refresh→retry behavior. The 5 onboarding components previously used
  // raw fetch with the persisted (possibly expired) token and silently
  // no-op'd on 401 — tour/checklist/goal banner vanished for the session.
  onboarding: (token: string) =>
    api<{
      welcome_seen: boolean;
      onboarding_goal?: string | null;
      [key: string]: unknown;
    }>("/users/me/onboarding", { token }),

  markWelcomeSeen: (token: string, goal?: string) =>
    api("/users/me/welcome-seen", {
      method: "POST",
      token,
      body: JSON.stringify(goal ? { goal } : {}),
    }),

  profile: (token: string) => api("/users/me/profile", { token }),

  updateProfile: (token: string, data: Record<string, unknown>) =>
    api("/users/me/profile", {
      method: "PUT",
      token,
      body: JSON.stringify(data),
    }),

  update: (token: string, data: { custom_product_pitch?: string | null }) =>
    api<{ id: string; custom_product_pitch?: string | null }>("/users/me", {
      method: "PUT",
      token,
      body: JSON.stringify(data),
    }),
};

// Interview endpoints
export const interviews = {
  list: (token: string) => api("/interviews", { token }),

  create: (
    token: string,
    data: {
      type?: string;
      difficulty?: string;
      num_questions?: number;
      job_offer_id?: string;
    }
  ) =>
    api("/interviews", { method: "POST", token, body: JSON.stringify(data) }),

  get: (token: string, id: string) => api(`/interviews/${id}`, { token }),

  start: (token: string, id: string) =>
    api(`/interviews/${id}/start`, { method: "POST", token }),

  submitAnswer: (
    token: string,
    sessionId: string,
    questionId: string,
    data: { user_response: string; response_time_seconds?: number }
  ) =>
    api(`/interviews/${sessionId}/questions/${questionId}/answer`, {
      method: "POST",
      token,
      body: JSON.stringify(data),
    }),

  complete: (token: string, id: string) =>
    api(`/interviews/${id}/complete`, { method: "POST", token }),
};

// Feedback endpoints
export const feedback = {
  submitBugReport: (
    data: {
      description: string;
      page_url?: string;
    },
    token?: string
  ) =>
    api<{ success: boolean; message: string }>("/feedback/bug-report", {
      method: "POST",
      token,
      body: JSON.stringify(data),
    }),
};

// Resume endpoints
export const resumes = {
  list: (token: string) => api("/resumes", { token }),

  upload: async (token: string, file: File): Promise<unknown> => {
    const doUpload = async (t: string) => {
      const formData = new FormData();
      formData.append("file", file);
      return fetch(`${API_URL}/api/v1/resumes/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${t}` },
        body: formData,
      });
    };

    let response = await doUpload(token);

    if (response.status === 401) {
      // Try refreshing the token
      if (!refreshPromise) {
        refreshPromise = tryRefreshToken().finally(() => { refreshPromise = null; });
      }
      const newToken = await refreshPromise;
      if (newToken) {
        response = await doUpload(newToken);
      } else {
        forceLogout();
        throw new ApiError("Session expirée", { status: 401, code: "session_expired", path: "/resumes/upload", method: "POST" });
      }
    }

    if (!response.ok) {
      if (response.status === 401) {
        forceLogout();
        throw new ApiError("Session expirée", { status: 401, code: "session_expired", path: "/resumes/upload", method: "POST" });
      }
      // detail can be a string OR an object ({message, usage,
      // upgrade_required} on the free-plan quota 403). The old
      // `new Error(error.detail)` stringified the object to
      // "[object Object]" and dropped the upgrade flag entirely.
      const error = await response.json().catch(() => ({}));
      const detail = error.detail;
      const message =
        typeof detail === "string"
          ? detail
          : detail?.message || "Erreur lors de l'upload";
      throw new ApiError(message, {
        status: response.status,
        code: typeof detail === "object" ? detail?.code : undefined,
        upgradeRequired: typeof detail === "object" ? Boolean(detail?.upgrade_required) : false,
        path: "/resumes/upload",
        method: "POST",
      });
    }

    return response.json();
  },

  delete: (token: string, id: string) =>
    api(`/resumes/${id}`, { method: "DELETE", token }),

  createBlank: (token: string) =>
    api<{ id: string; filename: string; source: string }>("/resumes/blank", {
      method: "POST",
      token,
    }),

  atsAnalysis: (
    token: string,
    resumeId: string,
    data: {
      job_offer_id?: string;
      job_title?: string;
      job_description?: string;
      job_requirements?: string;
      language?: "fr" | "en";
    }
  ) =>
    api<ATSAnalysisResult>(`/resumes/${resumeId}/ats-analysis`, {
      method: "POST",
      token,
      body: JSON.stringify(data),
    }),
};

// --- Cover letters --------------------------------------------------------

export interface CoverLetterSummary {
  id: string;
  title: string;
  language: string;
  tone: string;
  company: string | null;
  role: string | null;
  resume_id: string | null;
  job_offer_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface CoverLetterAnnotation {
  start: number;
  end: number;
  kind: "hook" | "alignment" | "proof" | "risk";
  severity: "info" | "warn" | "error";
  label: string;
  message: string;
}

export interface CoverLetterSubScore {
  value: number;
  label: string;
  detail: string;
}

export interface CoverLetterInsight {
  kind: string;
  tone: "positive" | "warn" | "info";
  title: string;
  detail: string;
}

export interface CoverLetterAnalysis {
  score_global: number;
  sub_scores: {
    alignment: CoverLetterSubScore;
    structure: CoverLetterSubScore;
    risks: CoverLetterSubScore;
  };
  annotations: CoverLetterAnnotation[];
  insights: CoverLetterInsight[];
  keywords_matched: string[];
  keywords_missing: string[];
  word_count: number;
}

export interface CoverLetterDetail extends CoverLetterSummary {
  content: string;
  generation_metadata: (Record<string, unknown> & {
    length?: string;
    contract_type?: string | null;
    analysis?: CoverLetterAnalysis;
  }) | null;
}

export type CoverLetterRefineAction =
  | "shorten"
  | "lengthen"
  | "tone_formal"
  | "tone_direct"
  | "translate_en"
  | "translate_fr";

export const coverLetters = {
  list: (token: string) =>
    api<CoverLetterSummary[]>("/cover-letters", { token }),

  get: (token: string, id: string) =>
    api<CoverLetterDetail>(`/cover-letters/${id}`, { token }),

  generate: (
    token: string,
    data: {
      resume_id?: string;
      job_offer_id?: string;
      raw_offer_text?: string;
      tone?: "formal" | "warm" | "assertive";
      language?: "fr" | "en";
      length?: "short" | "standard" | "long";
      contract_type?: "stage" | "alternance" | "cdi_junior" | "cdi" | "cadre";
      emphasized_experience?: string;
      personal_hook?: string;
      custom_note?: string;
    },
  ) =>
    api<CoverLetterDetail>("/cover-letters/generate", {
      method: "POST",
      token,
      body: JSON.stringify(data),
    }),

  update: (
    token: string,
    id: string,
    data: { title?: string; content?: string },
  ) =>
    api<CoverLetterDetail>(`/cover-letters/${id}`, {
      method: "PUT",
      token,
      body: JSON.stringify(data),
    }),

  refine: (token: string, id: string, action: CoverLetterRefineAction) =>
    api<CoverLetterDetail>(`/cover-letters/${id}/refine`, {
      method: "POST",
      token,
      body: JSON.stringify({ action }),
    }),

  delete: (token: string, id: string) =>
    api(`/cover-letters/${id}`, { method: "DELETE", token }),
};

// --- Applications (job tracker) ------------------------------------------

export type ApplicationStatus =
  | "interested"
  | "applied"
  | "first_contact"
  | "interviewing"
  | "offer"
  | "rejected"
  | "archived";

export type ApplicationPriority = "low" | "medium" | "high";

export type ApplicationSource =
  | "linkedin"
  | "indeed"
  | "wttj"
  | "apec"
  | "france_travail"
  | "glassdoor"
  | "company_website"
  | "referral"
  | "recruiter"
  | "other";

export type RemotePolicy = "onsite" | "hybrid" | "remote";

export type ApplicationEventType =
  | "status_change"
  | "note_added"
  | "reminder_set"
  | "email_sent"
  | "relance_sent"
  | "interview_scheduled"
  | "interview_completed"
  | "offer_received"
  | "doc_attached"
  | "created";

export interface ApplicationEvent {
  id: string;
  event_type: ApplicationEventType | string;
  occurred_at: string;
  label: string | null;
  payload: Record<string, unknown> | null;
}

export interface ApplicationSummary {
  id: string;
  status: ApplicationStatus;
  position: number;
  // Core
  source: string | null;
  source_url: string | null;
  priority: ApplicationPriority;
  remote_policy: RemotePolicy | null;
  notes: string | null;
  tags: string[] | null;
  // Dates
  applied_at: string | null;
  deadline: string | null;
  next_step_at: string | null;
  next_step_label: string | null;
  last_activity_at: string | null;
  // Salary
  salary_min: number | null;
  salary_max: number | null;
  salary_currency: string | null;
  salary_expected: string | null;
  // Contact
  contact_name: string | null;
  contact_email: string | null;
  contact_linkedin: string | null;
  // Ecosystem
  resume_id: string | null;
  cover_letter_id: string | null;
  ats_score: number | null;
  cover_letter_sent: boolean;
  // Ghosting flag (computed server-side)
  is_stale: boolean;
  days_since_activity: number | null;
  // Relations
  job_offer: {
    id: string;
    title: string;
    company: string | null;
    location: string | null;
    job_type: string | null;
  };
  created_at: string;
  updated_at: string;
}

export interface ApplicationAnalytics {
  total: number;
  by_status: Record<string, number>;
  by_source: Record<string, number>;
  funnel: {
    applied: number;
    first_contact: number;
    interviewing: number;
    offer: number;
  };
  stale_count: number;
  avg_response_days: number | null;
  volume_30d: { date: string; count: number }[];
}

export interface ApplicationCreatePayload {
  job_offer_id?: string;
  raw_offer_text?: string;
  status?: ApplicationStatus;
  notes?: string;
  source?: ApplicationSource | string;
  source_url?: string;
  priority?: ApplicationPriority;
  remote_policy?: RemotePolicy;
  deadline?: string;
  next_step_at?: string;
  next_step_label?: string;
  salary_min?: number;
  salary_max?: number;
  salary_currency?: string;
  salary_expected?: string;
  contact_name?: string;
  contact_email?: string;
  contact_linkedin?: string;
  tags?: string[];
  resume_id?: string;
  cover_letter_id?: string;
  ats_score?: number;
  cover_letter_sent?: boolean;
  applied_at?: string;
}

export type ApplicationUpdatePayload = Omit<
  ApplicationCreatePayload,
  "job_offer_id" | "raw_offer_text"
> & { position?: number };

export interface ApplicationListFilters {
  status?: ApplicationStatus;
  source?: string;
  priority?: ApplicationPriority;
  stale_only?: boolean;
  search?: string;
}

export const applications = {
  list: (token: string, filters?: ApplicationListFilters) => {
    const qs = new URLSearchParams();
    if (filters?.status) qs.set("status", filters.status);
    if (filters?.source) qs.set("source", filters.source);
    if (filters?.priority) qs.set("priority", filters.priority);
    if (filters?.stale_only) qs.set("stale_only", "true");
    if (filters?.search) qs.set("search", filters.search);
    const tail = qs.toString() ? `?${qs.toString()}` : "";
    return api<ApplicationSummary[]>(`/applications/${tail}`, { token });
  },

  analytics: (token: string) =>
    api<ApplicationAnalytics>("/applications/analytics", { token }),

  create: (token: string, data: ApplicationCreatePayload) =>
    api<ApplicationSummary>("/applications/", {
      method: "POST",
      token,
      body: JSON.stringify(data),
    }),

  get: (token: string, id: string) =>
    api<ApplicationSummary>(`/applications/${id}`, { token }),

  update: (token: string, id: string, data: ApplicationUpdatePayload) =>
    api<ApplicationSummary>(`/applications/${id}`, {
      method: "PUT",
      token,
      body: JSON.stringify(data),
    }),

  move: (
    token: string,
    id: string,
    data: { status: ApplicationStatus; position: number },
  ) =>
    api<ApplicationSummary>(`/applications/${id}/move`, {
      method: "POST",
      token,
      body: JSON.stringify(data),
    }),

  duplicate: (token: string, id: string) =>
    api<ApplicationSummary>(`/applications/${id}/duplicate`, {
      method: "POST",
      token,
    }),

  bulk: (
    token: string,
    data: {
      ids: string[];
      action: "archive" | "delete" | "set_status";
      status?: ApplicationStatus;
    },
  ) =>
    api<{ updated: number }>("/applications/bulk", {
      method: "POST",
      token,
      body: JSON.stringify(data),
    }),

  events: {
    list: (token: string, applicationId: string) =>
      api<ApplicationEvent[]>(`/applications/${applicationId}/events`, {
        token,
      }),

    create: (
      token: string,
      applicationId: string,
      data: {
        event_type: ApplicationEventType | string;
        label?: string;
        payload?: Record<string, unknown>;
        occurred_at?: string;
      },
    ) =>
      api<ApplicationEvent>(`/applications/${applicationId}/events`, {
        method: "POST",
        token,
        body: JSON.stringify(data),
      }),
  },

  delete: (token: string, id: string) =>
    api(`/applications/${id}`, { method: "DELETE", token }),
};

// --- LinkedIn optimizer --------------------------------------------------

export interface LinkedInExperience {
  title?: string;
  company?: string;
  duration?: string;
  location?: string;
  description?: string;
}

export interface LinkedInProfileData {
  full_name?: string;
  headline?: string;
  location?: string;
  about?: string;
  experiences?: LinkedInExperience[];
  skills?: string[];
  education?: { school?: string; degree?: string; period?: string; field?: string }[];
  languages?: { language?: string; level?: string }[];
  certifications?: string[];
  interests?: string[];
  raw_source?: string;
}

export interface LinkedInSectionReport {
  id: "headline" | "about" | "experiences" | "skills";
  label: string;
  score_out_of_10: number;
  diagnosis: string;
  current: string;
  suggested: string;
}

export interface LinkedInAnalysis {
  overall_score: number;
  summary: string;
  sections: LinkedInSectionReport[];
}

export interface LinkedInProfileResponse {
  id: string;
  source: "pdf" | "paste" | "manual" | null;
  profile_data: LinkedInProfileData | null;
  analysis_data: LinkedInAnalysis | null;
  analyzed_at: string | null;
  updated_at: string;
}

export const linkedin = {
  me: (token: string) =>
    api<LinkedInProfileResponse>("/linkedin/me", { token }),

  uploadPdf: async (token: string, file: File): Promise<LinkedInProfileResponse> => {
    const doUpload = async (t: string) => {
      const formData = new FormData();
      formData.append("file", file);
      return fetch(`${API_URL}/api/v1/linkedin/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${t}` },
        body: formData,
      });
    };

    let response = await doUpload(token);
    if (response.status === 401) {
      if (!refreshPromise) {
        refreshPromise = tryRefreshToken().finally(() => {
          refreshPromise = null;
        });
      }
      const newToken = await refreshPromise;
      if (newToken) {
        response = await doUpload(newToken);
      } else {
        forceLogout();
        throw new ApiError("Session expirée", { status: 401, code: "session_expired", path: "/linkedin/upload-pdf", method: "POST" });
      }
    }

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || "Upload failed");
    }
    return response.json();
  },

  paste: (token: string, raw_text: string) =>
    api<LinkedInProfileResponse>("/linkedin/paste", {
      method: "POST",
      token,
      body: JSON.stringify({ raw_text }),
    }),

  analyze: (token: string, data?: { target_role?: string; resume_id?: string }) =>
    api<LinkedInProfileResponse>("/linkedin/analyze", {
      method: "POST",
      token,
      body: JSON.stringify(data || {}),
    }),

  reset: (token: string) => api(`/linkedin/me`, { method: "DELETE", token }),
};

// Gamification endpoints
export interface StreakData {
  current_streak: number;
  longest_streak: number;
  last_activity_date: string | null;
  streak_freezes_available: number;
  total_active_days: number;
  calendar: { date: string; count: number }[];
}

export interface DailyChallengeData {
  id: string;
  challenge_date: string;
  challenge_type: string;
  title: string;
  description: string;
  question: string;
  difficulty: string;
  estimated_minutes: number;
  hints: string[] | null;
  is_completed: boolean;
}

export interface PlanStepData {
  id: string;
  day_offset: number;
  title: string;
  description: string;
  action_type: string;
  action_data: Record<string, unknown> | null;
  is_completed: boolean;
  completed_at: string | null;
}

export interface PreparationPlanData {
  id: string;
  total_days: number;
  plan_data: Record<string, unknown>;
  steps: PlanStepData[];
  created_at: string;
}

export interface UpcomingInterviewData {
  id: string;
  company_name: string;
  job_title: string;
  interview_date: string;
  interview_type: string | null;
  notes: string | null;
  status: string;
  days_remaining: number;
  preparation_plan: PreparationPlanData | null;
  created_at: string;
}

export interface BadgeData {
  id: string;
  name: string;
  name_en?: string | null;
  description: string;
  description_en?: string | null;
  icon: string;
  category: string;
  earned_at?: string | null;
  condition?: Record<string, unknown>;
}

export interface GamificationDashboardData {
  streak: StreakData;
  today_challenge: DailyChallengeData | null;
  upcoming_interviews: UpcomingInterviewData[];
  badges_earned_count: number;
  badges_total_count: number;
  recent_badges: BadgeData[];
}

export interface ChallengeCompletionData {
  id: string;
  challenge_id: string;
  user_response: string;
  ai_feedback: string | null;
  score: number | null;
  completed_at: string;
  new_badges: BadgeData[];
}

export const gamification = {
  dashboard: (token: string) =>
    api<GamificationDashboardData>("/gamification/dashboard", { token }),

  streak: (token: string) =>
    api<StreakData>("/gamification/streak", { token }),

  recordActivity: (token: string) =>
    api<{ current_streak: number; longest_streak: number; new_badges: BadgeData[] }>(
      "/gamification/streak/record",
      { method: "POST", token }
    ),

  todayChallenge: (token: string) =>
    api<DailyChallengeData>("/gamification/challenge/today", { token }),

  submitChallenge: (token: string, challengeId: string, data: { user_response: string }) =>
    api<ChallengeCompletionData>(`/gamification/challenge/${challengeId}/submit`, {
      method: "POST",
      token,
      body: JSON.stringify(data),
    }),

  listUpcoming: (token: string) =>
    api<UpcomingInterviewData[]>("/gamification/upcoming-interviews", { token }),

  createUpcoming: (
    token: string,
    data: {
      company_name: string;
      job_title: string;
      interview_date: string;
      interview_type?: string;
      notes?: string;
    }
  ) =>
    api<UpcomingInterviewData>("/gamification/upcoming-interviews", {
      method: "POST",
      token,
      body: JSON.stringify(data),
    }),

  deleteUpcoming: (token: string, id: string) =>
    api("/gamification/upcoming-interviews/" + id, { method: "DELETE", token }),

  completePlanStep: (token: string, stepId: string) =>
    api<{ status: string; new_badges: BadgeData[] }>(
      `/gamification/plan-steps/${stepId}/complete`,
      { method: "POST", token }
    ),

  badges: (token: string) =>
    api<{ earned: BadgeData[]; locked: BadgeData[] }>("/gamification/badges", { token }),
};

// Job Offer types
export interface JobOfferData {
  id: string;
  title: string;
  company?: string | null;
  location?: string | null;
  description?: string | null;
  requirements?: string | null;
  source_url?: string | null;
  job_type: string;
  parsed_data?: {
    required_skills?: string[];
    nice_to_have_skills?: string[];
    experience_level?: string;
    [key: string]: unknown;
  } | null;
  created_at: string;
}

export interface ParsedJobOffer {
  title: string;
  company?: string | null;
  location?: string | null;
  job_type: string;
  description: string;
  requirements: string;
  required_skills: string[];
  nice_to_have_skills: string[];
  experience_level: string;
}

export const jobOffers = {
  list: (token: string) =>
    api<JobOfferData[]>("/job-offers/", { token }),

  get: (token: string, id: string) =>
    api<JobOfferData>(`/job-offers/${id}`, { token }),

  create: (token: string, data: {
    title: string;
    company?: string;
    location?: string;
    description?: string;
    requirements?: string;
    source_url?: string;
    job_type?: string;
  }) =>
    api<JobOfferData>("/job-offers/", {
      method: "POST",
      token,
      body: JSON.stringify(data),
    }),

  update: (token: string, id: string, data: Record<string, unknown>) =>
    api<JobOfferData>(`/job-offers/${id}`, {
      method: "PUT",
      token,
      body: JSON.stringify(data),
    }),

  delete: (token: string, id: string) =>
    api(`/job-offers/${id}`, { method: "DELETE", token }),

  parse: (token: string, rawText: string, language: string = "fr") =>
    api<ParsedJobOffer>("/job-offers/parse", {
      method: "POST",
      token,
      body: JSON.stringify({ raw_text: rawText, language }),
    }),

  parseAndSave: (token: string, rawText: string, language: string = "fr") =>
    api<JobOfferData>("/job-offers/parse-and-save", {
      method: "POST",
      token,
      body: JSON.stringify({ raw_text: rawText, language }),
    }),
};

// ATS Analysis types (V2 with section scores and detailed tips)
export interface ATSMatchedKeyword {
  keyword: string;
  found_in: string;
  context: string;
  weight?: "critical" | "important" | "nice_to_have";
}

export interface ATSMissingKeyword {
  keyword: string;
  importance: "high" | "medium" | "low";
  suggestion: string;
  weight?: "critical" | "important" | "nice_to_have";
}

export interface ATSSuggestion {
  category: "skills" | "experience" | "education" | "format" | "summary" | "general";
  priority: "high" | "medium" | "low";
  title: string;
  detail: string;
  before: string;
  after: string;
}

export interface ATSSectionScores {
  experience: number;
  skills: number;
  education: number;
  format: number;
}

export interface ATSAnalysisResult {
  id?: string;
  score: number;
  matched_keywords: ATSMatchedKeyword[] | string[];
  missing_keywords: ATSMissingKeyword[] | string[];
  suggestions: ATSSuggestion[] | string[];
  strengths: string[];
  summary: string;
  is_premium?: boolean;
  job_title?: string;
  job_company?: string;
  job_offer_id?: string;
  resume_id?: string;
  created_at?: string;
  // V2 fields
  section_scores?: ATSSectionScores;
  format_issues?: string[];
  // V3 fields
  industry?: string;
  experience_match?: {
    required_years: string;
    candidate_years: string;
    seniority_gap: string;
    verdict: string;
  };
  keyword_density?: {
    total_unique_in_job: number;
    matched: number;
    coverage_percent: number;
  };
  hidden_count?: {
    matched_keywords: number;
    missing_keywords: number;
    suggestions: number;
    strengths: number;
    section_scores?: number;
    format_issues?: number;
  };
  // Redesign v2 fields (ats-results-redesign spec §4)
  annotation_hints?: Array<{
    type: "missing_keyword" | "format_quantify" | "format_missing_summary";
    section: "summary" | "experience" | "skills" | "education" | "format";
    keyword?: string;
    weight?: "critical" | "important" | "nice_to_have";
    message: string;
  }>;
  parsed_data?: {
    personal_info?: Record<string, unknown>;
    summary?: string;
    experiences?: Array<Record<string, unknown>>;
    skills?: string[] | Record<string, unknown>;
    education?: Array<Record<string, unknown>>;
    certifications?: Array<Record<string, unknown>>;
    projects?: Array<Record<string, unknown>>;
    languages?: string[] | Array<Record<string, unknown>>;
    name?: string;
  };
}

// Resume endpoints helpers
// CV-regeneration changes report (Phase 2). "keywords_woven_in" = surfaced from
// existing content; "keywords_to_add_if_true" = genuinely absent → the user adds
// them only if true (the honesty UX).
export interface RegenReport {
  keywords_woven_in: string[];
  keywords_to_add_if_true: { keyword: string; note?: string }[];
  rephrased: { section: string; before: string; after: string }[];
}

export interface RegenResult {
  id: string;
  parsed_data: Record<string, unknown>;
  report: RegenReport;
  source_analysis_id?: string;
}

export const atsAnalyses = {
  get: (token: string, analysisId: string) =>
    api<ATSAnalysisResult>(`/resumes/ats-analyses/${analysisId}`, { token }),

  list: (token: string) =>
    api<ATSAnalysisResult[]>(`/resumes/ats-analyses`, { token }),

  // Phase 2 — regenerate the CV (ATS-optimized, same style, honest). Returns the
  // new resume id + improved parsed_data + the changes report.
  regenerate: (token: string, resumeId: string, analysisId: string) =>
    api<RegenResult>(`/resumes/${resumeId}/ats/${analysisId}/regenerate`, {
      token,
      method: "POST",
    }),

  getRegeneration: (token: string, regenResumeId: string) =>
    api<RegenResult>(`/resumes/${regenResumeId}/regeneration`, { token }),
};

// One-time ATS-unlock payment (mode=payment). Returns a Stripe Checkout URL to
// redirect to; on success Stripe redirects back to the analysis page with
// ?unlock=success and the webhook flips `unlocked` + grants bundle credits.
export const stripeBilling = {
  createAtsUnlockSession: (token: string, analysisId: string) =>
    api<{ checkout_url: string }>(`/stripe/create-ats-unlock-session`, {
      token,
      method: "POST",
      body: JSON.stringify({ analysis_id: analysisId }),
    }),
};

// ============================================================================
// Training Hub (backend = /api/v1/practice/*, product label = "Training")
// ============================================================================

export type PracticeDifficulty = "easy" | "medium" | "hard";
export type PracticeStatus =
  | "created"
  | "in_progress"
  | "completed"
  | "abandoned"
  | "scored";
export type PracticeCategory = "cold_call";

export interface PracticeObjection {
  trigger: string;
  archetype: string;
  phrasing: string;
}

export interface PracticePersona {
  id: string;
  slug: string;
  name: string;
  role: string;
  company_context: string | null;
  photo_url: string | null;
  traits: string[] | null;
  objections: PracticeObjection[] | null;
  language: string;
  category: PracticeCategory;
  is_premium: boolean;
}

export interface PracticeScenario {
  id: string;
  slug: string;
  persona_id: string;
  title: string;
  category: PracticeCategory;
  difficulty: PracticeDifficulty;
  duration_target_seconds: number;
  context_briefing: string | null;
  expected_objections: string[] | null;
  scoring_focus: string[] | null;
  is_premium: boolean;
  is_active: boolean;
}

export interface PracticeCriterionScore {
  score: number;
  justification: string;
  transcript_quote: string;
  transcript_turn_index: number;
  advice: string;
  example: string | null;
}

export interface PracticeScoreBreakdown {
  opener: PracticeCriterionScore;
  pertinence: PracticeCriterionScore;
  discovery: PracticeCriterionScore;
  objection: PracticeCriterionScore;
  closing: PracticeCriterionScore;
}

export interface PracticeTurnAnnotation {
  turn_index: number;
  label: "good" | "neutral" | "weak";
  reason: string;
}

export interface PracticeVoiceMetricsCommentary {
  talk_ratio_comment: string;
  pace_comment: string;
  fillers_comment: string;
}

export interface PracticeInsights {
  turn_annotations: PracticeTurnAnnotation[];
  top_3_priorities: string[];
  breakdown_details: PracticeScoreBreakdown | null;
  short_call_skip: boolean;
}

export interface PracticeSession {
  id: string;
  user_id: string;
  scenario_id: string | null;
  persona_id: string | null;
  status: PracticeStatus;
  difficulty: PracticeDifficulty;
  language: string;
  custom_product_pitch: string | null;
  elevenlabs_conversation_id: string | null;
  transcript: unknown[] | null;
  audio_url: string | null;
  duration_seconds: number | null;
  overall_score: number | null;
  score_breakdown: PracticeScoreBreakdown | null;
  insights: PracticeInsights | null;
  voice_metrics: PracticeVoiceMetricsCommentary | null;
  started_at: string | null;
  ended_at: string | null;
  scored_at: string | null;
  created_at: string;
}

export interface PracticeQuota {
  used_this_month: number;
  limit: number | null; // null = unlimited (Premium / School / VIP)
  plan: string;
  upgrade_required: boolean;
}

export interface PracticeStartResponse {
  session_id: string;
  agent_id: string;
  persona_slug: string;
  signed_url: string;
  dynamic_variables: Record<string, string>;
  started_at: string;
}

export const practice = {
  quota: (token: string) =>
    api<PracticeQuota>("/practice/quota", { token }),

  listPersonas: (token: string, category?: PracticeCategory) =>
    api<PracticePersona[]>(
      `/practice/personas${category ? `?category=${category}` : ""}`,
      { token }
    ),

  getPersona: (token: string, slug: string) =>
    api<PracticePersona>(`/practice/personas/${slug}`, { token }),

  listScenarios: (
    token: string,
    filters?: {
      category?: PracticeCategory;
      difficulty?: PracticeDifficulty;
      persona_slug?: string;
    }
  ) => {
    const qs = new URLSearchParams();
    if (filters?.category) qs.set("category", filters.category);
    if (filters?.difficulty) qs.set("difficulty", filters.difficulty);
    if (filters?.persona_slug) qs.set("persona_slug", filters.persona_slug);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return api<PracticeScenario[]>(`/practice/scenarios${suffix}`, { token });
  },

  getScenario: (token: string, slug: string) =>
    api<PracticeScenario>(`/practice/scenarios/${slug}`, { token }),

  createSession: (
    token: string,
    payload: {
      scenario_id: string;
      difficulty?: PracticeDifficulty;
      language?: string;
      custom_product_pitch?: string;
    }
  ) =>
    api<PracticeSession>("/practice/sessions", {
      method: "POST",
      token,
      body: JSON.stringify(payload),
    }),

  startSession: (token: string, id: string) =>
    api<PracticeStartResponse>(`/practice/sessions/${id}/start`, {
      method: "POST",
      token,
    }),

  endSession: (
    token: string,
    id: string,
    payload?: {
      transcript?: unknown[];
      audio_url?: string;
      duration_seconds?: number;
      elevenlabs_conversation_id?: string;
    }
  ) =>
    api<PracticeSession>(`/practice/sessions/${id}/end`, {
      method: "POST",
      token,
      body: JSON.stringify(payload ?? {}),
    }),

  scoreSession: (token: string, id: string) =>
    api<PracticeSession>(`/practice/sessions/${id}/score`, {
      method: "POST",
      token,
    }),

  getSession: (token: string, id: string) =>
    api<PracticeSession>(`/practice/sessions/${id}`, { token }),

  listSessions: (token: string, params?: { skip?: number; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.skip != null) qs.set("skip", String(params.skip));
    if (params?.limit != null) qs.set("limit", String(params.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return api<PracticeSession[]>(`/practice/sessions${suffix}`, { token });
  },
};
